import React, { memo, useEffect, useState } from "react";
import {
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  SxProps,
  Theme,
  Grid,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DynamicFormField } from "@/models/dynamic-form.model";
import { InputFieldType } from "@/constants/enums/input-field-type.enum";
import PlusIcon from "@styles/all-images/icons/plusblue.svg";
import { FormikProps } from "formik";
import moment from "moment";
import dateHandler from "@/composables/dateUtil";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";

interface FormFieldProps {
  field: DynamicFormField;
  formik: FormikProps<any>;
  readOnly: boolean;
  datePickerSx: SxProps<Theme>;
}

const FormField = memo(
  ({ field, formik, readOnly, datePickerSx }: FormFieldProps) => {
    const [localField, setLocalField] = useState(field);
    const { toStandardDisplayDate } = dateHandler();

    const generateDynamicInputs = (
      field: DynamicFormField,
      nestedName?: string,
    ) => {
      const formikName = nestedName
        ? `${nestedName}.${field.name}`
        : field.name;

      const getNestedValue = (value: string, options: any) => {
        const nestedValues = value.split(".");
        return nestedValues.reduce(
          (acc, nestedValue) => acc?.[nestedValue],
          options,
        );
      };

      if (readOnly) {
        const value = getNestedValue(formikName, formik.values);
        console.log("value: ", value);
        console.log("formikName: ", formikName);
        let returnValue = null;

        switch (field.type) {
          case InputFieldType.CHECKBOX:
            returnValue = (
              <span className="tw-text-[#232323] tw-text-base">
                {value ? "Yes" : "No"}
              </span>
            );
            break;
          case InputFieldType.DATE:
            returnValue = (
              <span>
                {value !== "N/A"
                  ? toStandardDisplayDate({
                      inputDate: value,
                      inputFormat: "YYYY/MM/DD",
                      desiredFormat: "dateTime",
                    })
                  : "-"}
              </span>
            );
            break;
          case InputFieldType.ARRAY_OF_OBJECT:
            returnValue = (
              <div className="tw-flex tw-flex-col tw-gap-2">
                {field.children?.map((child) => {
                  console.log("child: ", child);
                  return (
                    <div key={child.name}>
                      {child.type === InputFieldType.ARRAY_OF_OBJECT ? (
                        <li className="tw-text-[#414651] tw-text-sm tw-font-semibold">
                          {child.label}
                        </li>
                      ) : (
                        <div className="tw-text-[#414651] tw-text-sm tw-font-semibold tw-ml-4">
                          {child.label}
                        </div>
                      )}
                      <div className="tw-ml-4">
                        {generateDynamicInputs(
                          child,
                          nestedName
                            ? `${nestedName}.${field.name}`
                            : field.name,
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
            break;
          default:
            returnValue = <span>{value || "-"}</span>;
            break;
        }
        return (
          <div className="tw-text-sm tw-text-[#232323] tw-break-words">
            {returnValue}
          </div>
        );
      }

      const commonProps = {
        name: formikName,
        value: getNestedValue(formikName, formik.values) || "",
        onChange: formik.handleChange,
        onBlur: formik.handleBlur,
        error:
          Boolean(getNestedValue(formikName, formik.touched)) &&
          Boolean(getNestedValue(formikName, formik.errors)),
        helperText:
          Boolean(getNestedValue(formikName, formik.touched)) &&
          (getNestedValue(formikName, formik.errors) as string),
        placeholder: field.placeholder,
      };

      switch (field.type) {
        case InputFieldType.DATE:
          return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={
                  getNestedValue(formikName, formik.values)
                    ? (() => {
                        const [day, month, year] = getNestedValue(
                          formikName,
                          formik.values,
                        ).split("-");
                        return new Date(`${year}-${month}-${day}`);
                      })()
                    : null
                }
                onChange={(newValue: Date | null) => {
                  if (newValue) {
                    const date = moment(new Date(newValue)).format(
                      "YYYY/MM/DD",
                    );
                    formik.setFieldValue(formikName, date);
                  } else {
                    formik.setFieldValue(formikName, null);
                  }
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: commonProps.error,
                    helperText: commonProps.helperText,
                    sx: datePickerSx,
                  },
                }}
              />
            </LocalizationProvider>
          );

        case InputFieldType.SELECT:
          return (
            <DynamicAutocomplete
              name={formikName}
              label={field.label}
              options={field.options || []}
              selectedValue={getNestedValue(formikName, formik.values)}
              onChange={(value) => formik.setFieldValue(formikName, value)}
              onBlur={formik.handleBlur}
              error={getNestedValue(formikName, formik.errors)}
              touched={getNestedValue(formikName, formik.touched)}
              required={field.required}
              placeholder={field.placeholder}
              hideLabel={true}
            />
          );

        case InputFieldType.CHECKBOX:
          return (
            <FormControlLabel
              control={
                <Checkbox
                  {...commonProps}
                  checked={getNestedValue(formikName, formik.values) as boolean}
                  sx={{
                    color: "#E9EAEB",
                    "&.Mui-checked": { color: "#146C94" },
                    ".PrivateSwitchBase-input": {
                      display: "none",
                    },
                  }}
                />
              }
              label={field.label}
            />
          );

        case InputFieldType.TEXTAREA:
          return (
            <textarea
              {...commonProps}
              className={`tw-w-full tw-px-3 tw-py-2 tw-border ${
                commonProps.error ? "tw-border-red-500" : "tw-border-[#E9EAEB]"
              } tw-rounded-lg focus:tw-outline-none focus:tw-border-[#146C94] tw-min-h-[100px] tw-resize-none`}
              rows={field.minRows || 3}
            />
          );

        case InputFieldType.ARRAY_OF_OBJECT:
          return (
            <div
              className={`${field.showAddButton ? "tw-bg-[#f0f6ff] tw-rounded-2xl tw-shadow-md tw-border tw-border-[#E9EAEB] tw-p-4 " : ""}`}
            >
              <Grid container spacing={2}>
                {field.children?.map((option) => {
                  return (
                    <Grid item sm={option.flexed ? 6 : 12}>
                      <div
                        key={option.name}
                        className={`tw-flex tw-flex-col tw-gap-2 ${
                          option.flexed ? "" : "tw-w-full"
                        }`}
                      >
                        {!field.showAddButton && (
                          <label className="tw-text-[#414651] tw-text-sm tw-font-medium">
                            {option.label}
                            {!readOnly && option.required && (
                              <span className="tw-text-red-500">*</span>
                            )}
                          </label>
                        )}
                        {generateDynamicInputs(
                          option,
                          nestedName
                            ? `${nestedName}.${field.name}`
                            : field.name,
                        )}
                        {!readOnly &&
                          !field.showAddButton &&
                          getNestedValue(formikName, formik.touched) &&
                          getNestedValue(formikName, formik.errors) && (
                            <div className="tw-text-red-500 tw-text-xs">
                              {getNestedValue(
                                `${formikName}.${option.name}`,
                                formik.errors,
                              )}
                            </div>
                          )}
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
              {field.showAddButton && (
                <Grid item sm={12}>
                  <span
                    className="tw-text-[#146C94] tw-font-medium tw-cursor-pointer tw-flex tw-items-center tw-mt-4"
                    onClick={onAddMore}
                  >
                    <img
                      src={PlusIcon}
                      alt="Plus Icon"
                      className="tw-w-8 tw-h-8"
                    />
                    Add More Shareholder
                  </span>
                </Grid>
              )}
            </div>
          );

        default:
          return (
            <input
              type={field.type}
              {...commonProps}
              className={`tw-w-full tw-px-3 tw-py-2 tw-border ${
                commonProps.error ? "tw-border-red-500" : "tw-border-[#E9EAEB]"
              } tw-rounded-lg focus:tw-outline-none focus:tw-border-[#146C94]`}
            />
          );
      }
    };

    const getNewNestedFieldObjects = (oldField: DynamicFormField) => {
      const newField = { ...oldField };
      const lastChild = newField.children?.slice?.(-1)?.[0];
      if (lastChild) {
        newField.children?.push({
          ...lastChild,
          name: `${lastChild.name.split("_")?.[0]}_${newField.children?.length + 1}`,
          label: `${lastChild.label.split(" ")?.[0]} ${newField.children?.length + 1}`,
        });
      }
      return newField;
    };

    const onAddMore = () => {
      const newField = getNewNestedFieldObjects(localField);
      setLocalField(newField);
    };

    useEffect(() => {
      setLocalField(field);
    }, [field]);

    return generateDynamicInputs(localField);
  },
);

FormField.displayName = "FormField";

export default FormField;
